import { createSlice } from '@reduxjs/toolkit';
import {
  loginUser,
  verifyOtp,
  registerUser,
  getUserProfile,
  updateUserProfile,
  socialLoginUser,
  logoutUser,
  initializeAuth,
} from './authAction';

const initialState = {
  // User data
  user: null,
  token: null,
  refreshToken: null,
  memberId: null,
  isAuthenticated: false,

  // Loading states
  loginLoading: false,
  otpLoading: false,
  signupLoading: false,
  profileLoading: false,
  socialLoginLoading: false,
  logoutLoading: false,
  initLoading: true,

  // Error states
  error: null,
  loginError: null,
  otpError: null,
  signupError: null,
  profileError: null,

  // OTP flow data
  pendingEmail: null,
  isRegistered: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Clear all errors
    clearErrors: (state) => {
      state.error = null;
      state.loginError = null;
      state.otpError = null;
      state.signupError = null;
      state.profileError = null;
    },

    // Clear specific error
    clearError: (state, action) => {
      const { field } = action.payload;
      if (field && state[`${field}Error`] !== undefined) {
        state[`${field}Error`] = null;
      }
    },

    // Set pending email for OTP flow
    setPendingEmail: (state, action) => {
      state.pendingEmail = action.payload;
    },

    // Reset auth state (manual logout)
    resetAuthState: (state) => {
      return { ...initialState, initLoading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loginLoading = true;
        state.loginError = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loginLoading = false;
        state.pendingEmail = action.meta.arg?.email || sessionStorage.getItem('pendingEmail');
        state.loginError = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loginLoading = false;
        state.loginError = action.payload;
        state.isAuthenticated = false;
      })

      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        const { accessToken, refreshToken, memberId, isRegistered, user } = action.payload || {};

        if (accessToken) {
          state.token = accessToken;
          state.refreshToken = refreshToken;
          state.memberId = memberId;
        }

        state.isRegistered = isRegistered;

        if (isRegistered && user) {
          state.user = user;
          state.isAuthenticated = true;
          state.pendingEmail = null;
        }

        state.otpError = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
        state.isAuthenticated = false;
      })

      // Register/Signup
      .addCase(registerUser.pending, (state) => {
        state.signupLoading = true;
        state.signupError = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.signupLoading = false;
        const { accessToken, refreshToken, memberId, user } = action.payload || {};

        if (accessToken) {
          state.token = accessToken;
          state.refreshToken = refreshToken;
          state.memberId = memberId;
          state.user = user;
          state.isAuthenticated = true;
          state.pendingEmail = null;
        }

        state.signupError = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.signupLoading = false;
        state.signupError = action.payload;
        state.isAuthenticated = false;
      })

      // Get Profile
      .addCase(getUserProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
        state.profileError = null;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })

      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload || state.user;
        state.profileError = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })

      // Social Login
      .addCase(socialLoginUser.pending, (state) => {
        state.socialLoginLoading = true;
        state.loginError = null;
      })
      .addCase(socialLoginUser.fulfilled, (state, action) => {
        state.socialLoginLoading = false;
        const { accessToken, refreshToken, memberId, user } = action.payload || {};

        if (accessToken) {
          state.token = accessToken;
          state.refreshToken = refreshToken;
          state.memberId = memberId;
          state.user = user;
          state.isAuthenticated = true;
        }

        state.loginError = null;
      })
      .addCase(socialLoginUser.rejected, (state, action) => {
        state.socialLoginLoading = false;
        state.loginError = action.payload;
        state.isAuthenticated = false;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.logoutLoading = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      .addCase(logoutUser.fulfilled, () => {
        return { ...initialState, isAuthenticated: false, user: null, token: null, refreshToken: null, initLoading: false };
      })
      .addCase(logoutUser.rejected, () => {
        return { ...initialState, isAuthenticated: false, user: null, token: null, refreshToken: null, initLoading: false };
      })

      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.initLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.initLoading = false;
        const { isAuthenticated, user, token, refreshToken, memberId } = action.payload || {};

        if (isAuthenticated) {
          state.isAuthenticated = true;
          state.user = user;
          state.token = token;
          state.refreshToken = refreshToken;
          state.memberId = memberId;
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.initLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      });
  },
});

export const { clearErrors, clearError, setPendingEmail, resetAuthState } = authSlice.actions;

export default authSlice.reducer;
